/**
 * Rock Off Retail — mega-menu hover intent (17 Aug fix).
 *
 * Problem: `.ro-mega` panels are centred under the whole nav bar
 * (`.ro-nav`, the nearest positioned ancestor — see the CSS comment on
 * `.ro-mega`), not under their own trigger. For a trigger sitting away from
 * the nav's horizontal centre (Band at the left, New at the far right —
 * see also .ro-mega--new's own right-anchor fix in
 * assets/section-rockoff-header.css), a mouse travelling diagonally down
 * from the trigger to the panel crosses real horizontal daylight where it's
 * hovering neither the trigger `<li>` nor the panel — plain CSS `:hover`
 * (even with the small vertical hover-bridge already on `.ro-nav__has-mega`)
 * drops out during that crossing and the panel snaps shut before the
 * pointer arrives.
 *
 * Fix: a small hover-intent layer. `.is-open` is added the instant the
 * pointer enters EITHER the trigger `<li>` or its `.ro-mega` panel, and only
 * removed after a short grace delay (CLOSE_DELAY_MS) with the pointer
 * outside BOTH — cancelled immediately if the pointer re-enters either
 * before the delay elapses. This covers the dead space between trigger and
 * panel by TIME rather than trying to compute its exact geometry (which
 * changes with viewport width, trigger position and column count), so it
 * works the same for every mega, not just the worst-offset ones.
 *
 * `:hover` and the new `:focus-within` CSS rules (see
 * assets/section-rockoff-header.css) are left in place as an unconditional,
 * JS-independent fallback: `.is-open` is an OR-condition alongside them,
 * never a replacement — a visitor with JS disabled, or a keyboard user
 * (focus-within has no "dead space" concept at all: Tab jumps straight
 * between focusable elements, never crossing empty screen space), gets the
 * exact same panel with zero behaviour change from this file.
 *
 * Scope: desktop hover mega-menus only (`.ro-nav__has-mega`, shown under
 * `@media (any-hover: hover) and (any-pointer: fine)` — widened from
 * `(hover: hover) and (pointer: fine)` 17 Aug, see the gate in `init()`
 * below and the matching CSS query in section-rockoff-header.css) — the
 * mobile drawer (assets/rockoff-mobile-menu.js) is a completely separate,
 * unrelated component and untouched by this file.
 */
(function () {
  'use strict';

  var CLOSE_DELAY_MS = 300;

  function initTrigger(trigger) {
    var mega = trigger.querySelector(':scope > .ro-mega');
    if (!mega) return;

    var closeTimer = null;

    function open() {
      if (closeTimer) {
        clearTimeout(closeTimer);
        closeTimer = null;
      }
      trigger.classList.add('is-open');
    }

    function scheduleClose() {
      if (closeTimer) clearTimeout(closeTimer);
      closeTimer = setTimeout(function () {
        trigger.classList.remove('is-open');
        closeTimer = null;
      }, CLOSE_DELAY_MS);
    }

    trigger.addEventListener('mouseenter', open);
    trigger.addEventListener('mouseleave', scheduleClose);
    // Belt-and-braces: the panel is `.ro-mega`, a descendant of `trigger`,
    // so a `mouseenter`/`mouseleave` bound to `trigger` already fires for
    // pointer movement onto/off the panel too (it's all one hoverable
    // subtree) — these two extra listeners exist only so the SAME open()/
    // scheduleClose() logic runs even if the panel is ever portalled/moved
    // outside its trigger's DOM subtree in a future edit; harmless no-ops
    // today since the events already bubble from `trigger` itself.
    mega.addEventListener('mouseenter', open);
    mega.addEventListener('mouseleave', scheduleClose);
  }

  function init() {
    // BUGFIX (17 Aug Sunday sweep, item 1 hardening): matches the same
    // any-hover/any-pointer widening applied to the CSS in
    // assets/section-rockoff-header.css — see that file's comment for the
    // full rationale (hybrid touchscreen+mouse Windows laptops can report
    // pointer:fine false on the primary pointer alone). Keeping the JS
    // gate and the CSS media query in sync matters here: if this gate is
    // stricter than the CSS, the hover-intent grace timer never attaches
    // even on a device where the CSS :hover rule itself would work fine.
    if (!window.matchMedia('(any-hover: hover) and (any-pointer: fine)').matches) return;
    var triggers = document.querySelectorAll('.ro-nav__has-mega');
    triggers.forEach(initTrigger);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
